'use client';

import { useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import type { BulkPayload } from '@/types/bulk-action';
import { getSortingStateParser } from '@/lib/parsers';
import { Plus, Pencil, Trash2, Loader2, Box } from 'lucide-react';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { SelectAllBanner } from '@/components/select-all-banner';
import { PermissionGate } from '@/features/auth';
import { BOX_TYPES_HASH } from '../constants';
import { boxTypeQueries } from '../services/box-types.queries';
import { useDeleteBoxTypeMutation } from '../services/box-types.mutations';
import { BoxTypeFormDialog } from './box-type-form-dialog';
import { BoxTypesTableActionBar } from './box-types-table-action-bar';
import { BoxTypeCard } from './box-type-card';
import { BoxTypeCompactRow } from './box-type-compact-row';
import type { BoxType, BoxTypesListParams } from '../types';

export function BoxTypeListing() {
  const t = useTranslations('boxTypes');
  // Overlay open-state lives in the URL hash (#create, #edit/<id>,
  // #delete/<id>) so overlays are deep-linkable and close on browser Back.
  // Destructure the stable callbacks so they can go in hook dep arrays without
  // tripping the React Compiler's manual-memoization check.
  const { open: createOpen, onOpenChange: toggleCreate } = useHashDialog(
    BOX_TYPES_HASH.create
  );
  const {
    open: editOpen,
    param: editId,
    openWith: openEdit,
    onOpenChange: closeEdit
  } = useHashParamDialog(BOX_TYPES_HASH.edit);
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(BOX_TYPES_HASH.delete);
  const [view, setView] = useDataView('box-types');

  const columns: ColumnDef<BoxType>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('name')} />
      ),
      cell: ({ row, column }) => (
        <span className='font-medium'>
          <HighlightMatch
            text={row.original.name}
            query={getColumnSearchTerms(column)}
          />
        </span>
      ),
      enableColumnFilter: true,
      meta: {
        label: t('searchPlaceholder'),
        variant: 'text' as const,
        placeholder: t('searchPlaceholder'),
        permission: 'box_types.column.name'
      }
    },
    {
      id: 'code',
      accessorKey: 'code',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('code')} />
      ),
      cell: ({ row }) => (
        <span className='font-mono text-sm'>{row.original.code}</span>
      ),
      meta: { label: t('code'), permission: 'box_types.column.code' }
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('description')} />
      ),
      cell: ({ row }) => (
        <span className='text-muted-foreground text-sm'>
          {row.original.description ?? '—'}
        </span>
      ),
      enableSorting: false,
      meta: {
        label: t('description'),
        permission: 'box_types.column.description'
      }
    },
    {
      id: 'maximumCapacity',
      accessorKey: 'max_capacity',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('maximumCapacity')} />
      ),
      cell: ({ row }) => (
        <span className='font-mono text-sm'>{row.original.max_capacity}</span>
      ),
      meta: {
        label: t('maximumCapacity'),
        permission: 'box_types.column.maximumCapacity'
      }
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('status')} />
      ),
      cell: ({ row }) => (
        <Badge
          variant={row.original.status === 'active' ? 'default' : 'secondary'}
          className={cn(
            row.original.status === 'active'
              ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
              : 'bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-200'
          )}
        >
          {row.original.status === 'active'
            ? t('statusActive')
            : t('statusInactive')}
        </Badge>
      ),
      enableSorting: false,
      meta: { label: t('status'), permission: 'box_types.column.status' }
    },
    {
      id: 'actions',
      header: () => <div className='text-end'>{t('actions')}</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className='flex items-center justify-end gap-2'>
            <PermissionGate permissions={['box_types.edit']}>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => handleEdit(item)}
              >
                <Pencil className='h-4 w-4' />
              </Button>
            </PermissionGate>
            <PermissionGate permissions={['box_types.delete']}>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => handleDelete(item)}
              >
                <Trash2 className='text-destructive h-4 w-4' />
              </Button>
            </PermissionGate>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false
    }
  ];

  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState('name', parseAsString.withDefault(''));
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<BoxType>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<BoxType>[])
  );

  const queryParams = useMemo<BoxTypesListParams>(() => {
    const params: BoxTypesListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch) {
      params.search = urlSearch;
    }
    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';
    return params;
  }, [urlPage, urlPerPage, urlSearch, urlSorting]);

  const { data, isPending, isPlaceholderData } = useQuery(
    boxTypeQueries.list(queryParams)
  );
  const deleteMutation = useDeleteBoxTypeMutation();

  const boxTypes = (data?.data ?? []) as BoxType[];
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  // Resolve the hash id param back to the loaded row (row must be on the
  // current page; deep-linking an off-page id simply won't open the overlay).
  const editingItem = editOpen
    ? (boxTypes.find((b) => String(b.id) === editId) ?? null)
    : null;
  const deleteTarget = deleteOpen
    ? (boxTypes.find((b) => String(b.id) === deleteId) ?? null)
    : null;

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<BoxType>({
    columns: filteredColumns,
    pageKey: 'box-types',
    data: boxTypes,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);

  const getBulkPayload = useCallback((): BulkPayload => {
    if (selectAll.isAllPagesSelected) {
      const { page, per_page, sort_by, sort_direction, ...filters } =
        queryParams;
      return { mode: 'all', filters };
    }
    const selectedIds = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    return { mode: 'ids', ids: selectedIds };
  }, [selectAll.isAllPagesSelected, queryParams, table]);

  const handleBulkComplete = useCallback(() => {
    selectAll.reset();
  }, [selectAll]);

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('name'), value: 'name' },
      { label: t('code'), value: 'code' },
      { label: t('createdAt'), value: 'created_at' }
    ],
    [t]
  );

  const handleEdit = useCallback(
    (item: BoxType) => openEdit(String(item.id)),
    [openEdit]
  );

  const handleDelete = useCallback(
    (item: BoxType) => openDelete(String(item.id)),
    [openDelete]
  );

  function handleCreate() {
    toggleCreate(true);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    const force = (deleteTarget.boxes_count ?? 0) > 0;
    try {
      await deleteMutation.mutateAsync({ id: deleteTarget.id, force });
    } catch {
      // handled by global MutationCache
    }
    closeDelete(false);
  }

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div />
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='box-types'
          />
          <PermissionGate permissions={['box_types.create']}>
            <Button onClick={handleCreate}>
              <Plus className='me-2 h-4 w-4' />
              {t('addNew')}
            </Button>
          </PermissionGate>
        </div>
      </div>

      <DataTableToolbar
        table={table}
        viewFilterPermission='box_types.view_filter'
      />

      {isPending ? (
        <ListingSkeleton view={view} />
      ) : boxTypes.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <Box className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noBoxTypes')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {boxTypes.map((boxType) => (
              <BoxTypeCard
                key={boxType.id}
                boxType={boxType}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {boxTypes.map((boxType) => (
              <BoxTypeCompactRow
                key={boxType.id}
                boxType={boxType}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <SelectAllBanner
            isAllPagesSelected={selectAll.isAllPagesSelected}
            isCurrentPageFullySelected={selectAll.isCurrentPageFullySelected}
            currentPageCount={boxTypes.length}
            totalCount={pagination?.total ?? 0}
            pageSize={tableState.pagination.pageSize}
            onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
            onClearSelection={() => selectAll.reset()}
          />
          <DataTable
            table={table}
            actionBar={
              <BoxTypesTableActionBar
                table={table}
                getBulkPayload={getBulkPayload}
                onComplete={handleBulkComplete}
                isAllPagesSelected={selectAll.isAllPagesSelected}
                totalCount={pagination?.total ?? 0}
              />
            }
          />
        </div>
      )}

      <BoxTypeFormDialog
        open={createOpen || (editOpen && !!editingItem)}
        onOpenChange={(open) => {
          if (!open) (editOpen ? closeEdit : toggleCreate)(false);
        }}
        boxType={editOpen ? editingItem : null}
      />

      <AlertDialog
        open={deleteOpen && !!deleteTarget}
        onOpenChange={closeDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirmDescription', {
                name: deleteTarget?.name ?? ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {deleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
